import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";

const textVariants = cva("m-0 font-sans antialiased", {
  variants: {
    variant: {
      /* Sizes from tailwind.config.js (MUI typography parity with src/theme.ts) */
      h2: "text-mui-h2 font-light",
      h3: "text-mui-h3 font-lighter",
      h4: "text-mui-h4 font-normal",
      h6: "text-mui-h6 font-normal",
      body1: "text-lg font-normal",
      caption: "text-sm font-normal",
      overline: "text-mui-overline font-normal uppercase",
      inherit: "font-inherit text-inherit leading-inherit",
    },
    align: {
      inherit: "",
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
    gutterBottom: {
      true: "mb-[0.35em]",
      false: "",
    },
    color: {
      unset: "",
      inherit: "text-inherit",
      primary: "text-primary",
      secondary: "text-secondary",
    },
  },
  defaultVariants: {
    variant: "body1",
    align: "inherit",
    gutterBottom: false,
    color: "unset",
  },
});

const defaultComponentByVariant: Record<
  NonNullable<VariantProps<typeof textVariants>["variant"]>,
  React.ElementType
> = {
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h6: "h6",
  body1: "p",
  caption: "span",
  overline: "span",
  inherit: "span",
};

export type TextVariant = NonNullable<VariantProps<typeof textVariants>["variant"]>;

export type TextAlign = NonNullable<VariantProps<typeof textVariants>["align"]>;

export type TextColor = "inherit" | "primary" | "secondary";

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  variant?: TextVariant;
  component?: React.ElementType;
  align?: TextAlign;
  color?: TextColor;
  gutterBottom?: boolean;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  {
    variant = "body1",
    component,
    align = "inherit",
    color,
    gutterBottom = false,
    className,
    children,
    ...rest
  },
  ref
) {
  const Comp = component ?? defaultComponentByVariant[variant];
  const colorKey = color === undefined ? "unset" : color;

  return (
    <Comp
      ref={ref as never}
      className={clsx(
        textVariants({ variant, align, gutterBottom, color: colorKey }),
        className
      )}
      {...rest}
    >
      {children}
    </Comp>
  );
});

Text.displayName = "Text";
